import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import DatePicker from './DatePicker';
import { Upload, X, DollarSign, Calendar, FileText } from 'lucide-react';

const EnhancedTaskDialog = ({ open, onOpenChange, onSave, initialData = null, mode = 'create' }) => {
  const [formData, setFormData] = useState({
    // Core fields
    taskType: 'renewal',
    state: '',
    complianceCategory: 'license',
    itemName: '',
    title: '',
    description: '',
    
    // Date fields
    issueDate: '',
    effectiveDate: '',
    expirationDate: '',
    renewalDueDate: '',
    
    // Financial tracking
    filingFee: '',
    paymentStatus: 'unpaid',
    paymentDate: '',
    
    // Renewal logic
    renewalCycle: 'annual',
    customCycleDays: '',
    reminderThresholds: '60,30,15,7', // Days before expiration
    
    // Assignment and priority
    assignedTo: '',
    priority: 'medium',
    status: 'open',
    
    // Links
    relatedEntityId: '',
    relatedEntityType: '',
    
    // Documents
    uploadedDocuments: []
  });

  const [documentFile, setDocumentFile] = useState(null);

  const taskTypes = ['Renewal', 'New Application', 'Filing', 'Reporting', 'Other'];
  const states = ['Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 
    'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana',
    'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana',
    'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 
    'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 
    'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 
    'Wisconsin', 'Wyoming', 'District of Columbia'];
  
  const complianceCategories = [
    'License', 
    'Registration', 
    'Registered Agent', 
    'Filing', 
    'Annual Report',
    'Statement of Information',
    'Foreign Qualification',
    'DBA',
    'Other'
  ];

  const renewalCycles = [
    { value: 'annual', label: 'Annual (1 year)' },
    { value: 'biennial', label: 'Biennial (2 years)' },
    { value: 'triennial', label: 'Triennial (3 years)' },
    { value: 'custom', label: 'Custom' }
  ];

  const paymentStatuses = ['Unpaid', 'Paid', 'Pending', 'Reimbursed'];
  const priorities = ['Low', 'Medium', 'High', 'Urgent'];

  useEffect(() => {
    if (initialData) {
      setFormData({ ...formData, ...initialData });
    }
  }, [initialData]);

  // Auto-generate title based on selections
  useEffect(() => {
    if (formData.taskType && formData.itemName && formData.state) {
      const action = formData.taskType === 'renewal' ? 'Renew' : 
                    formData.taskType === 'new application' ? 'Apply for' : 'Complete';
      const title = `${action} ${formData.itemName} - ${formData.state}`;
      setFormData(prev => ({ ...prev, title }));
    }
  }, [formData.taskType, formData.itemName, formData.state]);

  // Auto-calculate renewal due date if cycle and expiration date are set
  useEffect(() => {
    if (formData.expirationDate && formData.renewalCycle) {
      const expDate = new Date(formData.expirationDate);
      let daysOffset = 60; // Default 60 days before expiration
      
      if (formData.renewalCycle === 'annual') {
        daysOffset = 60;
      } else if (formData.renewalCycle === 'biennial') {
        daysOffset = 90;
      } else if (formData.renewalCycle === 'triennial') {
        daysOffset = 120;
      } else if (formData.renewalCycle === 'custom' && formData.customCycleDays) {
        daysOffset = Math.min(parseInt(formData.customCycleDays) / 4, 120);
      }
      
      const renewalDue = new Date(expDate);
      renewalDue.setDate(renewalDue.getDate() - daysOffset);
      setFormData(prev => ({ ...prev, renewalDueDate: renewalDue.toISOString().split('T')[0] }));
    }
  }, [formData.expirationDate, formData.renewalCycle, formData.customCycleDays]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title || !formData.state || !formData.complianceCategory) {
      alert('Please fill in all required fields');
      return;
    }
    
    // Pass form data AND document file to parent
    onSave({
      ...formData,
      documentFile: documentFile,  // Pass the actual File object
      uploadedDocuments: documentFile ? [documentFile.name] : formData.uploadedDocuments
    });
  };

  const handleDocumentUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setDocumentFile(file);
      // In production, upload to backend/S3 here
    }
  };

  const handleCancel = () => {
    setFormData({
      taskType: 'renewal',
      state: '',
      complianceCategory: 'license',
      itemName: '',
      title: '',
      description: '',
      issueDate: '',
      effectiveDate: '',
      expirationDate: '',
      renewalDueDate: '',
      filingFee: '',
      paymentStatus: 'unpaid',
      paymentDate: '',
      renewalCycle: 'annual',
      customCycleDays: '',
      reminderThresholds: '60,30,15,7',
      assignedTo: '',
      priority: 'medium',
      status: 'open',
      relatedEntityId: '',
      relatedEntityType: '',
      uploadedDocuments: []
    });
    setDocumentFile(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Create Compliance Task' : 'Edit Compliance Task'}</DialogTitle>
          <DialogDescription>
            Track licenses, registrations, filings, and renewals with full compliance metadata
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Core Classification */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="taskType">Task Type *</Label>
              <Select value={formData.taskType} onValueChange={(val) => setFormData({ ...formData, taskType: val })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {taskTypes.map(type => (
                    <SelectItem key={type.toLowerCase().replace(' ', '-')} value={type.toLowerCase().replace(' ', '-')}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="complianceCategory">Compliance Category *</Label>
              <Select value={formData.complianceCategory} onValueChange={(val) => setFormData({ ...formData, complianceCategory: val })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {complianceCategories.map(cat => (
                    <SelectItem key={cat.toLowerCase().replace(' ', '-')} value={cat.toLowerCase().replace(' ', '-')}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="state">State *</Label>
              <Select value={formData.state} onValueChange={(val) => setFormData({ ...formData, state: val })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {states.map(state => (
                    <SelectItem key={state} value={state}>
                      {state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="itemName">Item Name *</Label>
              <Input
                id="itemName"
                value={formData.itemName}
                onChange={(e) => setFormData({ ...formData, itemName: e.target.value })}
                placeholder="e.g., Insurance Agency License"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Task Title (auto-generated)</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Auto-generated based on selections"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description / Notes</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Additional details, requirements, or notes"
              rows={3}
            />
          </div>

          {/* Date Fields */}
          <div className="border-t pt-4">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Date Tracking
            </h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="issueDate">Issue Date</Label>
                <DatePicker
                  id="issueDate"
                  value={formData.issueDate}
                  onChange={(val) => setFormData({ ...formData, issueDate: val })}
                  placeholder="MM/DD/YYYY"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="effectiveDate">Effective Date</Label>
                <DatePicker
                  id="effectiveDate"
                  value={formData.effectiveDate}
                  onChange={(val) => setFormData({ ...formData, effectiveDate: val })}
                  placeholder="MM/DD/YYYY"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="expirationDate">Expiration Date</Label>
                <DatePicker
                  id="expirationDate"
                  value={formData.expirationDate}
                  onChange={(val) => setFormData({ ...formData, expirationDate: val })}
                  placeholder="MM/DD/YYYY"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="renewalDueDate">Renewal Due Date (auto-calculated)</Label>
                <DatePicker
                  id="renewalDueDate"
                  value={formData.renewalDueDate}
                  onChange={(val) => setFormData({ ...formData, renewalDueDate: val })}
                  placeholder="MM/DD/YYYY"
                />
              </div>
            </div>
          </div>

          {/* Financial Tracking */}
          <div className="border-t pt-4">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Financial Tracking
            </h3>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="filingFee">Filing Fee / Cost</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="filingFee"
                    type="number"
                    step="0.01"
                    value={formData.filingFee}
                    onChange={(e) => setFormData({ ...formData, filingFee: e.target.value })}
                    className="pl-7"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="paymentStatus">Payment Status</Label>
                <Select value={formData.paymentStatus} onValueChange={(val) => setFormData({ ...formData, paymentStatus: val })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentStatuses.map(status => (
                      <SelectItem key={status.toLowerCase()} value={status.toLowerCase()}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="paymentDate">Payment Date</Label>
                <DatePicker
                  id="paymentDate"
                  value={formData.paymentDate}
                  onChange={(val) => setFormData({ ...formData, paymentDate: val })}
                  placeholder="MM/DD/YYYY"
                />
              </div>
            </div>
          </div>

          {/* Renewal Logic */}
          <div className="border-t pt-4">
            <h3 className="font-semibold mb-4">Renewal Logic</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="renewalCycle">Renewal Cycle</Label>
                <Select value={formData.renewalCycle} onValueChange={(val) => setFormData({ ...formData, renewalCycle: val })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {renewalCycles.map(cycle => (
                      <SelectItem key={cycle.value} value={cycle.value}>
                        {cycle.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {formData.renewalCycle === 'custom' && (
                <div className="space-y-2">
                  <Label htmlFor="customCycleDays">Custom Cycle (days)</Label>
                  <Input
                    id="customCycleDays"
                    type="number"
                    value={formData.customCycleDays}
                    onChange={(e) => setFormData({ ...formData, customCycleDays: e.target.value })}
                    placeholder="365"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="reminderThresholds">Reminder Thresholds (days)</Label>
                <Input
                  id="reminderThresholds"
                  value={formData.reminderThresholds}
                  onChange={(e) => setFormData({ ...formData, reminderThresholds: e.target.value })}
                  placeholder="60,30,15,7"
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">Comma-separated days before expiration</p>
              </div>
            </div>
          </div>

          {/* Document Upload */}
          <div className="border-t pt-4">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Document Attachments
            </h3>
            <div className="space-y-2">
              <Label htmlFor="documentUpload" className="cursor-pointer">
                <div className="border-2 border-dashed rounded-lg p-6 text-center hover:bg-muted/50 transition-colors">
                  <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Click to upload invoice, certificate, or filing confirmation
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">PDF, PNG, JPG up to 10MB</p>
                </div>
                <Input
                  id="documentUpload"
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={handleDocumentUpload}
                  className="hidden"
                />
              </Label>
              {documentFile && (
                <div className="flex items-center justify-between p-2 bg-muted rounded">
                  <span className="text-sm">{documentFile.name}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setDocumentFile(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Assignment and Priority */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select value={formData.priority} onValueChange={(val) => setFormData({ ...formData, priority: val })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {priorities.map(p => (
                    <SelectItem key={p.toLowerCase()} value={p.toLowerCase()}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="assignedTo">Assigned To</Label>
              <Input
                id="assignedTo"
                value={formData.assignedTo}
                onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })}
                placeholder="Email address"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="submit">
              {mode === 'create' ? 'Create Task' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EnhancedTaskDialog;
